import './awsconfig';
import vuetify from './plugins/vuetify';
import '@/assets/scss/styles.scss';
import 'vue-slider-component/theme/default.css';
import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
// registerServiceWorker.js relied on serviceworker-webpack-plugin to emit sw.js.
// That plugin was removed during the Webpack 5 / Node 22 migration.
// SW functionality was also gated behind 'isDemo' mode (src/utils/sw.js) so it
// was never active. Re-enable once a Workbox-based replacement is in place.
// import './registerServiceWorker.js';
import DeviceDetector from 'device-detector-js';
import AppDialogA from '@/components/dialogs/ClipDialogA.vue';
import VueCookies from 'vue-cookies';

import '@/directives/validations';

import VCalendar from 'v-calendar';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import VModal from 'vue-js-modal';
import VueSlider from 'vue-slider-component';
import Timeline from '@/components/Timeline.vue';
import * as filter from '@/directives/filters';

import Skeleton from '@/components/skeletons/Skeleton.vue';
import NoImage from '@/components/NoImage.vue';
import PendingClip from '@/components/PendingClip.vue';
import CountryPhoneCode from '@/components/inputs/CountryPhoneCode.vue';
import CustomCheck from '@/components/inputs/CustomCheck.vue';
import AppTextField from '@/components/inputs/AppTextField.vue';
import CustomCheck2 from '@/components/inputs/CustomCheck2.vue';
import ResizablePage from '@/components/ResizablePage.vue';
import Emoji from '@/components/Emoji.vue';
import SkeletonBox from '@/components/SkeletonBox.vue';
import VideoSkeleton from '@/components/skeletons/VideoSkeleton.vue';
import ListSkeleton from '@/components/skeletons/ListSkeleton.vue';
import VideoStream from '@/components/VideoStream.vue';
import VideoSeek from '@/components/VideoSeek.vue';
import CustomTooltip from '@/components/CustomTooltip.vue';
import EventLabel from '@/components/EventLabel.vue';
import Paginate from '@/components/Paginate.vue';
import CustomTabs from '@/components/CustomTabs.vue';
import { prettyLog } from './utils/prettyLog';

Vue.config.productionTip = false;

prettyLog.addDefaultLogStyle();

// detect device
const deviceDetector = new DeviceDetector();
const detector = deviceDetector.parse(window.navigator.userAgent);
Vue.prototype.$device = {
  isMobile: detector.device.type == 'smartphone',
  isIOS: detector.os.name == 'iOS',
  isAndroid: detector.os.name == 'Android',
  isSafari: detector.client.name == 'Safari',
  osName: detector.os.name,
  deviceType: detector.device.type,
  browser: detector.client.name,
};

Vue.prototype.$log = {
  info(...messages) {
    prettyLog.info(messages.join('\n'));
  },
  bold(...messages) {
    prettyLog.bold(messages.join('\n'));
  },
  error(...messages) {
    prettyLog.error(messages.join('\n'));
  },
};

Vue.filter('time', filter.time);
Vue.filter('date', filter.date);
Vue.filter('dateMs', filter.dateMs);

// Register the component globally
Vue.component('ValidationProvider', ValidationProvider);
Vue.component('ValidationObserver', ValidationObserver);
Vue.component('Timeline', Timeline);
Vue.component('VueSlider', VueSlider);
Vue.component('NoImage', NoImage);
Vue.component('PendingClip', PendingClip);
Vue.component('AppCheck', CustomCheck);
Vue.component('AppTextField', AppTextField);
Vue.component('AppCheck2', CustomCheck2);
Vue.component('ResizablePage', ResizablePage);
Vue.component('SkeletonBox', SkeletonBox);
Vue.component('SkeletonVideo', VideoSkeleton);
Vue.component('SkeletonList', ListSkeleton);
Vue.component('VideoStream', VideoStream);
Vue.component('VideoSeek', VideoSeek);
Vue.component('Tooltip', CustomTooltip);
Vue.component('EventLabel', EventLabel);
Vue.component('CountryCode', CountryPhoneCode);
Vue.component('AppDialogA', AppDialogA);
Vue.component('AppEmoji', Emoji);
Vue.component('AppTabs', CustomTabs);
Vue.component('Paginate', Paginate);

Vue.mixin({
  methods: {
    /**
     * Avoids redundand error when navigating to already active page
     */
    routerPush(route) {
      return this.$router.push(route).catch((error) => {
        if (error.name != 'NavigationDuplicated') {
          throw error;
        }
      });
    },
    /**
     * Avoids redundand error when navigating to already active page on replace
     */
    routerReplace(route) {
      this.$router.replace(route).catch((error) => {
        if (error.name != 'NavigationDuplicated') {
          throw error;
        }
      });
    },
    /**
     * Delay promise
     * @param {*} ms
     * @returns
     */
    delayPromise(ms) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, ms);
      });
    },
    routerPushLogin() {
      if (this.$device.isAndroid) {
        window.location.replace(process.env.VUE_APP_ANDROID_URL);
      } else if (this.$device.isIOS) {
        this.handleIOSDeepLink();
      } else {
        this.routerPush({ name: 'login' });
      }
    },
    routerPushLoginMobile() {
      if (this.$device.isAndroid) {
        window.location.replace(process.env.VUE_APP_ANDROID_URL);
      } else if (this.$device.isIOS) {
        this.handleIOSDeepLink();
      }
    },
    handleIOSDeepLink() {
      if (window.opener) {
        // Check if the page was opened by another app (e.g. Gmail, Teams)
        window.close();
        return;
      }
      const openedApp = window.open(process.env.VUE_APP_IOS_URL, '_blank');
      const checkAppInForeground = () => {
        if (document.hidden) {
          // Show App Store by URL
          window.location.href = process.env.VUE_APP_IOS_STORE_URL;
        } else if (openedApp && !openedApp.closed) {
          // If the application is available, open it and close tab
          openedApp.close();
        }
      };
      // Check if the app is in the foreground every 100 milliseconds
      const checkInterval = setInterval(checkAppInForeground, 100);
      // Clear the interval after a certain duration
      const timeoutDuration = 5000; // Adjust the duration as needed
      setTimeout(() => {
        clearInterval(checkInterval);
      }, timeoutDuration);
    },
  },
});

Vue.use(VueCookies);
Vue.use(VModal, { dynamic: true });
Vue.use(VCalendar, {
  componentPrefix: 'vc', // Use <vc-calendar /> instead of <v-calendar />
  locale: 'ja',
});
Vue.component('AppSkeleton', Skeleton);

new Vue({
  router,
  store,
  vuetify,
  render: (h) => h(App),
}).$mount('#app');
